import { type SelectionRange } from '../../../state/types.ts';
import { type SelectionAxis } from '../../types.ts';

import { getRangeMinMax } from './get-range-min-max.ts';

type IsIndexAdjacentToRangeParams = {
  range: SelectionRange;
  index: number;
  axis: SelectionAxis;
};

type FindRangesAdjacentToIndexParams = {
  ranges: SelectionRange[];
  index: number;
  axis: SelectionAxis;
};

/**
 * Checks if an index is adjacent to a selection range for the given axis.
 * An index is adjacent if it's exactly one index before the start or after the end.
 * @param range - The selection range to check
 * @param index - The index to check
 * @param axis - The axis to check ('row' or 'col')
 * @returns True if the index is adjacent to the range, false otherwise
 */
function isIndexAdjacentToRange({
  range,
  index,
  axis
}: IsIndexAdjacentToRangeParams): boolean {
  const { min, max } = getRangeMinMax(range, axis);
  return index === min - 1 || index === max + 1;
}

/**
 * Finds all selection ranges that are adjacent to the given index for the given axis.
 * Time Complexity: O(n) where n is the number of ranges
 * @param ranges - Array of selection ranges to search
 * @param index - The index to check adjacency for
 * @param axis - The axis to check ('row' or 'col')
 * @returns Array of ranges adjacent to the index
 */
export function findRangesAdjacentToIndex({
  ranges,
  index,
  axis
}: FindRangesAdjacentToIndexParams): SelectionRange[] {
  return ranges.filter((range) => isIndexAdjacentToRange({ range, index, axis }));
}
